/* eslint-disable no-empty-pattern */
import {Fragment, useContext} from 'react'

import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import LayoutGrid, {LayoutGridItem} from '@s-ui/react-layout-grid'
import MoleculeAccordion, {
  moleculeAccordionBehavior,
  moleculeAccordionHeaderLabelWraps,
  MoleculeAccordionItem
} from '@s-ui/react-molecule-accordion'
import IconAccordionCollapsed from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'
import IconAccordionExpanded from '@adv-mt/mt-svg-icons/lib/ChevronUp.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {publishYourAdQuestions, publishYourAdTrackingEvents} from './faqs-content.js'

const PANEL_FAQS_TEST_ID = 'mt-PanelFaqs'

const PanelFaqs = ({questions, trackingEvents, faqsStructuredData, withFaqsStructuredData = true}) => {
  const {i18n} = useContext(Context)
  const {track} = useAnalyticsTrack()

  const faqsSchema =
    faqsStructuredData ||
    JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: questions.map(faqItem => ({
        '@type': 'Question',
        name: i18n.t(faqItem.question),
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${i18n.t(faqItem.answer, faqItem.propsSchema)}${
            faqItem.list ? `<ol>${i18n.t(faqItem.list, faqItem.listPropsSchema)}</ol>` : ''
          }`
        }
      }))
    })

  const onToggleFaqsTab = trackingEvents
    ? (event, {isExpanded, value}) => {
        const {contractingEvent, expandingEvent} = trackingEvents

        track({
          event: isExpanded ? expandingEvent : contractingEvent,
          data: {hit_information: questions[value].trackingHitInformation}
        })
      }
    : () => {}

  return (
    <div className="mt-PanelFaqs" data-testid={PANEL_FAQS_TEST_ID}>
      <div>
        <MoleculeAccordion
          onChange={onToggleFaqsTab}
          as={Fragment}
          behavior={moleculeAccordionBehavior.MULTIPLE}
          headerIconCollapsed={
            <IconAccordionCollapsed aria-hidden="true" role="presentation" size={ATOM_ICON_SIZES.medium} />
          }
          headerIconExpanded={
            <IconAccordionExpanded aria-hidden="true" role="presentation" size={ATOM_ICON_SIZES.medium} />
          }
        >
          <LayoutGrid xxs={12}>
            {questions.map((faqItem, index) => (
              <LayoutGridItem key={faqItem.question}>
                <MoleculeAccordionItem
                  as="div"
                  headerAs="h3"
                  headerLevel={3}
                  label={i18n.t(faqItem.question)}
                  headerLabelWrap={moleculeAccordionHeaderLabelWraps.WRAP}
                  value={index}
                  content={
                    <div className="mt-PanelFaqs-accordionContent">
                      <p className="mt-PanelFaqs-accordionContentAnswer">
                        {htmlStringToReactElement(i18n.t(faqItem.answer, faqItem.props))}
                      </p>
                      {faqItem.list && <ol>{htmlStringToReactElement(i18n.t(faqItem.list, faqItem.listProps))}</ol>}
                    </div>
                  }
                />
              </LayoutGridItem>
            ))}
          </LayoutGrid>
        </MoleculeAccordion>
      </div>
      {withFaqsStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: faqsSchema
          }}
        />
      )}
    </div>
  )
}

PanelFaqs.displayName = 'PanelFaqs'
PanelFaqs.propTypes = {
  faqsStructuredData: PropTypes.string,
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      answer: PropTypes.string.isRequired,
      list: PropTypes.string,
      listProps: PropTypes.object,
      listPropsSchema: PropTypes.object,
      props: PropTypes.object,
      propsSchema: PropTypes.object,
      question: PropTypes.string.isRequired,
      trackingHitInformation: PropTypes.string
    }).isRequired
  ).isRequired,
  trackingEvents: PropTypes.shape({
    expandingEvent: PropTypes.string,
    contractingEvent: PropTypes.string
  }),
  withFaqsStructuredData: PropTypes.bool
}

export {
  publishYourAdQuestions as panelFaqsPublishYourAdQuestions,
  publishYourAdTrackingEvents as panelFaqsPublishYourAdTrackingEvents
}
export default PanelFaqs
